import * as React from "react";
import { Menu, PanelLeft, Search } from "lucide-react";

import { Button } from "components/common/buttons/Button";
import { useSidebar } from "components/common/Sidebar/Sidebar";
import { BreadcrumbNavigation } from "components/common/BreadcrumbNavigation";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/common/TooltipV3";
import { useNavigationSearch } from "components/layout/shell/GlobalNavigationSearch";
import { cn } from "utils/cn";

interface AppHeaderProps {
  /**
   * Replaces the default breadcrumb, e.g. the project and draft switchers
   * inside a project. Defaults to the route breadcrumb.
   */
  breadcrumb?: React.ReactNode;
  /** Context-specific controls rendered on the right side of the header. */
  actions?: React.ReactNode;
}

/** Sizing for the header's ghost icon Buttons, matching the comments and connect buttons. */
export const headerIconButtonClassName =
  "h-8 w-8 shrink-0 rounded-md p-0 text-muted-foreground hover:text-foreground";

const SEARCH_LABEL = "Search";
const SEARCH_SHORTCUT = "⌘K";

/**
 * Opens the navigation palette, the same as Cmd/Ctrl+K. A search field from
 * md up, an icon button below md.
 */
export const HeaderSearch = ({ compact }: { compact: boolean }) => {
  const search = useNavigationSearch();
  if (!search) return null;
  // The mobile sheet gate still applies: every open goes through `openSearch`.
  const { openSearch } = search;

  if (compact) {
    return (
      <Tooltip delayDuration={400}>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={openSearch}
            aria-label={SEARCH_LABEL}
            aria-keyshortcuts="Meta+K Control+K"
            className={headerIconButtonClassName}
          >
            <Search aria-hidden />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" sideOffset={6}>
          {`${SEARCH_LABEL} (${SEARCH_SHORTCUT})`}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <button
      type="button"
      onClick={openSearch}
      aria-keyshortcuts="Meta+K Control+K"
      className={cn(
        // Dark mode: a shade below the header, so it reads as an inset field in every preset.
        "flex h-8 w-40 shrink-0 items-center gap-2 rounded-md border border-border bg-background px-2 text-left text-xs text-muted-foreground transition-colors dark:bg-black/25 xl:w-52",
        "hover:border-muted-foreground/50 hover:text-foreground",
        "outline-none focus-visible:ring-2 focus-visible:ring-primary"
      )}
    >
      <Search className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="flex-1">{SEARCH_LABEL}</span>
      <kbd className="rounded border border-border px-1 font-sans text-[10px] leading-4">
        {SEARCH_SHORTCUT}
      </kbd>
    </button>
  );
};

/**
 * Top bar of every app context. Desktop shows the sidebar toggle (Cmd/Ctrl+B);
 * below md it shows the menu button that opens the sidebar as a sheet.
 */
export const AppHeader = ({ breadcrumb, actions }: AppHeaderProps) => {
  const { isMobile, open, openMobile, setOpenMobile, toggleSidebar } = useSidebar();

  return (
    <header className="app-chrome flex h-[54px] shrink-0 items-center gap-2 bg-card px-3 md:px-4">
      <div className="flex min-w-0 items-center gap-2">
        {isMobile ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setOpenMobile(true)}
            aria-label="Open navigation"
            aria-expanded={openMobile}
            className={headerIconButtonClassName}
          >
            <Menu aria-hidden />
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
            aria-expanded={open}
            title={open ? "Collapse sidebar (⌘B)" : "Expand sidebar (⌘B)"}
            className={cn(headerIconButtonClassName, "-ml-1")}
          >
            <PanelLeft aria-hidden />
          </Button>
        )}
        <div className="min-w-0">{breadcrumb ?? <BreadcrumbNavigation />}</div>
      </div>
      <div className="min-w-1 flex-1" />
      <div className="flex shrink-0 items-center gap-2 md:gap-3">
        <HeaderSearch compact={isMobile} />
        {actions}
      </div>
    </header>
  );
};
